import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUpload, FaSave, FaSpinner } from 'react-icons/fa';
import PageHeader from '../../molecules/PageHeader';
import { toast } from 'react-toastify';
import { createBatchProcessing, CreateBatchRequest, getColumnTemplates, saveColumnTemplate } from '../../../services/CreditAnalysisService';
import { unmaskDocument, maskDocument, validateDocument } from './utils/documentUtils';
import ActionButton from './components/ActionButton';
import AssignorSearch, { LegalPersonResult } from './components/form/AssignorSearch';
import SaveTemplateDialog from './components/form/SaveTemplateDialog';
import TagManager, { TagKey, TAGS } from './components/form/TagManager';
import DataPreviewTable from './components/form/DataPreviewTable';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS & VARIABLES
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════


interface AssignorData {
    id: string;
    name: string;
    data: string[][];
    columnTags: Record<number, TagKey>;
    validationErrors: Map<number, Set<number>>;
}

interface GroupedItem {
    document: string;
    name: string;
    amount: number;
    dueDate?: string;
    obs?: string;
    count: number;
}

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

const getCnpjRoot = (cnpj: string): string => cnpj.slice(0, 8);

const isCnpjMatrix = (cnpj: string): boolean => cnpj.slice(8, 12) === '0001';

const getGroupingKey = (doc: string): string => {
    if (doc.length === 14) return getCnpjRoot(doc);
    return doc;
};

const parseBRNumber = (value: string): number => {
    const cleaned = value.replaceAll(/[^\d,.-]/g, '');
    // Has comma → BR decimal separator
    if (cleaned.includes(',')) {
        return Number.parseFloat(cleaned.replaceAll('.', '').replace(',', '.')) || 0;
    }
    // No comma but has dots: check if dots are thousands separators (groups of 3 digits)
    if (cleaned.includes('.') && /^\d{1,3}(\.\d{3})+$/.test(cleaned)) {
        return Number.parseFloat(cleaned.replaceAll('.', '')) || 0;
    }
    return Number.parseFloat(cleaned) || 0;
};

const groupRowsByDocument = (
    rows: string[][],
    tagColumns: Record<string, number>
): Map<string, GroupedItem> => {
    const grouped = new Map<string, GroupedItem>();

    rows.forEach(row => {
        const doc = unmaskDocument(row[tagColumns['PRODUCER_DOCUMENT']] || '');
        const name = row[tagColumns['DRAWEE']] || '';
        const amount = parseBRNumber(row[tagColumns['AMOUNT']] || '0');
        const dueDate = tagColumns['DUE_DATE'] !== undefined ? row[tagColumns['DUE_DATE']] : undefined;
        const obs = tagColumns['OBSERVATION'] !== undefined ? row[tagColumns['OBSERVATION']] : undefined;

        if (!doc) return;

        const key = getGroupingKey(doc);
        const existing = grouped.get(key);

        if (existing) {
            existing.amount += amount;
            existing.count += 1;
            if (obs && !existing.obs) existing.obs = obs;
            // For CNPJs: prefer the matrix document (branch 0001)
            if (doc.length === 14 && isCnpjMatrix(doc)) {
                existing.document = doc;
                existing.name = name;
            }
        } else {
            grouped.set(key, { document: doc, name, amount, dueDate, obs, count: 1 });
        }
    });

    return grouped;
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function CreditAnalysisPageForm() {
    const navigate = useNavigate();
    const [batchName, setBatchName] = useState('');
    const [assignor, setAssignor] = useState<AssignorData>({
        id: 'assignor-1',
        name: '',
        data: [],
        columnTags: {},
        validationErrors: new Map()
    });
    const [draggedTag, setDraggedTag] = useState<TagKey | null>(null);
    const [savedTemplates, setSavedTemplates] = useState<Record<string, Record<number, TagKey>>>({});
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [isValidated, setIsValidated] = useState(false);

    const [selectedPerson, setSelectedPerson] = useState<LegalPersonResult | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        getColumnTemplates().then(data => setSavedTemplates(data as any)).catch(() => { });
    }, []);

    // Validação reativa
    useEffect(() => {
        if (assignor.data.length === 0 || Object.keys(assignor.columnTags).length === 0) {
            setAssignor(prev => ({ ...prev, validationErrors: new Map() }));
            return;
        }

        const errors = new Map<number, Set<number>>();

        assignor.data.forEach((row, rowIndex) => {
            const errorColumns = new Set<number>();

            Object.entries(assignor.columnTags).forEach(([colIndex, tagKey]) => {
                const col = Number.parseInt(colIndex);
                const value = row[col]?.trim() || '';

                if ((tagKey === 'PRODUCER_DOCUMENT' || tagKey === 'DRAWEE' || tagKey === 'AMOUNT') && !value) {
                    errorColumns.add(col);
                }

                if (tagKey === 'PRODUCER_DOCUMENT' && value && !validateDocument(value)) {
                    errorColumns.add(col);
                }

                if (tagKey === 'AMOUNT' && value) {
                    if (parseBRNumber(value) <= 0) {
                        errorColumns.add(col);
                    }
                }
            });

            if (errorColumns.size > 0) {
                errors.set(rowIndex, errorColumns);
            }
        });

        setAssignor(prev => ({ ...prev, validationErrors: errors }));
    }, [assignor.data, assignor.columnTags]);

    const handleSelectAssignor = (person: LegalPersonResult) => {
        setSelectedPerson(person);
        setAssignor(prev => ({ ...prev, id: person.personId, name: person.legalName }));
    };

    const clearAssignor = () => {
        setSelectedPerson(null);
        setAssignor(prev => ({ ...prev, id: 'assignor-1', name: '' }));
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const rows = pastedText.split('\n').filter(row => row.trim());
        const parsedData = rows
            .map(row => row.split('\t'))
            .filter(row => row.some(cell => cell.trim() !== '')); // Remove linhas completamente vazias

        setAssignor(prev => ({ ...prev, data: parsedData, columnTags: {} }));
        setIsValidated(false);

        toast.info(`${parsedData.length} linhas importadas`);
    };

    const handleDragStart = (tagKey: TagKey) => {
        setDraggedTag(tagKey);
    };

    const handleDrop = (colIndex: number) => {
        if (!draggedTag) return;

        const prevColIndex = Object.keys(assignor.columnTags).find(
            key => assignor.columnTags[Number(key)] === draggedTag
        );

        const newTags = { ...assignor.columnTags };
        if (prevColIndex) delete newTags[Number(prevColIndex)];
        newTags[colIndex] = draggedTag;

        setAssignor(prev => ({ ...prev, columnTags: newTags }));

        setDraggedTag(null);
    };

    const removeTag = (colIndex: number) => {
        const newTags = { ...assignor.columnTags };
        delete newTags[colIndex];
        setAssignor(prev => ({ ...prev, columnTags: newTags }));
    };


    const removeRow = (rowIndex: number) => {
        // Atualizar índices de erro
        const newErrors = new Map<number, Set<number>>();
        assignor.validationErrors.forEach((errorCols, rowIdx) => {
            if (rowIdx < rowIndex) {
                newErrors.set(rowIdx, errorCols);
            } else if (rowIdx > rowIndex) {
                newErrors.set(rowIdx - 1, errorCols);
            }
            // rowIdx === rowIndex é removido (não adiciona)
        });

        setAssignor(prev => ({
            ...prev,
            data: prev.data.filter((_, i) => i !== rowIndex),
            validationErrors: newErrors
        }));
    };

    const removeColumn = (colIndex: number) => {
        const newData = assignor.data.map(row => row.filter((_, j) => j !== colIndex));
        const newTags: Record<number, TagKey> = {};

        Object.keys(assignor.columnTags).forEach(key => {
            const idx = Number.parseInt(key);
            if (idx < colIndex) newTags[idx] = assignor.columnTags[idx];
            if (idx > colIndex) newTags[idx - 1] = assignor.columnTags[idx];
        });

        // Atualizar índices de erro
        const newErrors = new Map<number, Set<number>>();
        assignor.validationErrors.forEach((errorCols, rowIdx) => {
            const newErrorCols = new Set<number>();
            errorCols.forEach(col => {
                if (col < colIndex) newErrorCols.add(col);
                if (col > colIndex) newErrorCols.add(col - 1);
                // col === colIndex é removido (não adiciona)
            });
            if (newErrorCols.size > 0) {
                newErrors.set(rowIdx, newErrorCols);
            }
        });

        setAssignor(prev => ({ ...prev, data: newData, columnTags: newTags, validationErrors: newErrors }));
    };

    const saveTemplate = (name: string) => {
        if (Object.keys(assignor.columnTags).length === 0) {
            toast.error('Configure as tags antes de salvar');
            return;
        }

        const newTemplates = { ...savedTemplates, [name]: assignor.columnTags };
        setSavedTemplates(newTemplates);
        saveColumnTemplate(name, assignor.columnTags).catch(() => { });
        toast.success(`Template "${name}" salvo com sucesso`);
        setShowSaveDialog(false);
    };

    const loadTemplate = (name: string) => {
        if (savedTemplates[name]) {
            setAssignor(prev => ({ ...prev, columnTags: savedTemplates[name] }));
            toast.info(`Template "${name}" carregado`);
        }
    };

    const clearData = () => {
        setAssignor(prev => ({ ...prev, data: [] }));
        toast.info('Dados limpos');
    };

    const clearTags = () => {
        setAssignor(prev => ({ ...prev, columnTags: {} }));
        toast.info('Tags removidas');
    };

    const handleValidate = () => {
        const requiredTags: TagKey[] = ['PRODUCER_DOCUMENT', 'DRAWEE', 'AMOUNT'];
        const assignedTags = Object.values(assignor.columnTags);
        const missingTags = requiredTags.filter(tag => !assignedTags.includes(tag));
        if (missingTags.length > 0) {
            toast.error(`Tags obrigatórias faltando: ${missingTags.map(t => TAGS[t].name).join(', ')}`);
            return;
        }
        if (assignor.validationErrors.size > 0) {
            toast.error(`Existem ${assignor.validationErrors.size} linha(s) com erro. Corrija antes de validar.`);
            return;
        }

        // Map column tags to indices
        const tagColumns: Record<string, number> = {};
        Object.entries(assignor.columnTags).forEach(([col, tag]) => {
            tagColumns[tag] = Number.parseInt(col);
        });

        // Group by document (CPF: exact match, CNPJ: root 8 digits keeping matrix)
        const grouped = groupRowsByDocument(assignor.data, tagColumns);

        // Rebuild data with consolidated rows
        const docColIdx = tagColumns['PRODUCER_DOCUMENT'];
        const nameColIdx = tagColumns['DRAWEE'];
        const amountColIdx = tagColumns['AMOUNT'];
        const consolidatedData: string[][] = [];
        let mergedCount = 0;

        Array.from(grouped.values()).forEach((info) => {
            const row = new Array(assignor.data[0]?.length || 3).fill('');
            const doc = info.document;
            row[docColIdx] = maskDocument(doc);
            row[nameColIdx] = info.name;
            row[amountColIdx] = info.amount.toFixed(2);
            if (tagColumns['DUE_DATE'] !== undefined && info.dueDate) row[tagColumns['DUE_DATE']] = info.dueDate;
            if (tagColumns['OBSERVATION'] !== undefined && info.obs) row[tagColumns['OBSERVATION']] = info.obs;
            consolidatedData.push(row);
            if (info.count > 1) mergedCount++;
        });

        setAssignor(prev => ({ ...prev, data: consolidatedData, validationErrors: new Map() }));
        setIsValidated(true);

        if (mergedCount > 0) {
            toast.success(`Validado: ${consolidatedData.length} sacados (${mergedCount} agrupados por CPF (exato) / CNPJ (raiz))`);
        } else {
            toast.success(`Validado: ${consolidatedData.length} sacados`);
        }
    };

    const handleSubmit = async () => {
        if (!batchName.trim()) {
            toast.error('Digite o nome do lote');
            return;
        }

        const requiredTags: TagKey[] = ['PRODUCER_DOCUMENT', 'DRAWEE', 'AMOUNT'];

        if (!assignor.name.trim()) {
            toast.error('Selecione um cedente');
            return;
        }

        if (assignor.data.length === 0) {
            toast.error('Importe os dados antes de processar');
            return;
        }

        const assignedTags = Object.values(assignor.columnTags);
        const missingTags = requiredTags.filter(tag => !assignedTags.includes(tag));

        if (missingTags.length > 0) {
            toast.error(`Tags obrigatórias faltando: ${missingTags.map(t => TAGS[t].name).join(', ')}`);
            return;
        }

        if (assignor.validationErrors.size > 0) {
            toast.error(`Existem ${assignor.validationErrors.size} linha(s) com erro`);
            return;
        }

        // Map column tags to indices
        const tagColumns: Record<string, number> = {};
        Object.entries(assignor.columnTags).forEach(([col, tag]) => {
            tagColumns[tag] = Number.parseInt(col);
        });

        // Build API payload (deduplicate: CPF exact match, CNPJ by root keeping matrix)
        const grouped = groupRowsByDocument(assignor.data, tagColumns);
        const items = Array.from(grouped.values()).map(info => ({
            document: info.document,
            draweeName: info.name,
            requestedAmount: info.amount,
            dueDate: info.dueDate || undefined,
            observation: info.obs || undefined,
        }));

        const request: CreateBatchRequest = {
            batchName,
            assignor: {
                uuid: assignor.id,
                name: assignor.name,
                document: selectedPerson?.govTaxId || '',
            },
            items,
        };

        try {
            setIsSubmitting(true);
            toast.info('Enviando lote para processamento...');
            const result = await createBatchProcessing(request);
            toast.success(`Lote "${result.batchNumber}" criado com ${result.totalItems} análises — status: ${result.status}`);
            navigate('/credit-analysis/processamentos');
        } catch (error: any) {
            toast.error(`Erro ao criar lote: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="px-4 pt-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 min-h-screen">
            <PageHeader
                title="Novo Processamento de Análise de Crédito"
                apiVersion="Credit Analysis API - 1.0.0-mock"
                breadcrumb={[
                    { label: 'Análise de Crédito', url: '/credit-analysis/processamentos' },
                    { label: 'Novo Processamento', url: '#' }
                ]}
            >
                <div className="flex gap-2 pt-6">
                    <ActionButton variant="cancel" onClick={() => navigate('/credit-analysis/processamentos')}>
                        Cancelar
                    </ActionButton>
                    {assignor.data.length > 0 && !isValidated && (
                        <ActionButton variant="warning" onClick={handleValidate}>
                            Validar Dados
                        </ActionButton>
                    )}
                    {isValidated && (
                        <ActionButton variant="primary" onClick={handleSubmit} disabled={isSubmitting} icon={isSubmitting ? <FaSpinner className="animate-spin" /> : <FaSave />}>
                            {isSubmitting ? 'Processando...' : 'Processar Lote'}
                        </ActionButton>
                    )}
                </div>
            </PageHeader>

            <div className="mt-6 space-y-6">
                {/* Nome do Lote */}
                <div className="bg-gray-50 dark:bg-slate-700 p-4 rounded-lg border border-gray-200 dark:border-slate-600">
                    <label className="block text-sm font-semibold mb-2 dark:text-gray-100">
                        Nome do Lote *
                    </label>
                    <input
                        type="text"
                        value={batchName}
                        onChange={(e) => setBatchName(e.target.value)}
                        placeholder="Ex: Processamento Grande - 100 Sacados - 15/01/2026"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-slate-800 dark:text-gray-100"
                    />
                </div>

                {/* Cedente */}
                <div className="bg-gray-50 dark:bg-slate-700 p-6 rounded-lg border-2 border-gray-300 dark:border-slate-600">
                    <AssignorSearch
                        selectedPerson={selectedPerson}
                        onSelect={handleSelectAssignor}
                        onClear={clearAssignor}
                    />

                    <TagManager
                        columnTags={assignor.columnTags}
                        savedTemplates={savedTemplates}
                        hasData={assignor.data.length > 0}
                        onClearData={clearData}
                        onClearTags={clearTags}
                        onShowSaveDialog={() => setShowSaveDialog(true)}
                        onLoadTemplate={loadTemplate}
                        onDragStart={handleDragStart}
                    />

                    {/* Área de colagem */}
                    {assignor.data.length === 0 && (
                        <div
                            contentEditable
                            onPaste={handlePaste}
                            suppressContentEditableWarning
                            className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center bg-white dark:bg-slate-800 cursor-text focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 mb-4"
                        >
                            <FaUpload className="mx-auto text-3xl text-gray-400 dark:text-gray-500 mb-2" />
                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                                Clique aqui e cole os dados do Excel (Ctrl+V ou Cmd+V)
                            </p>
                        </div>
                    )}

                    {/* Tabela de dados */}
                    {assignor.data.length > 0 && (
                        <DataPreviewTable
                            data={assignor.data}
                            columnTags={assignor.columnTags}
                            validationErrors={assignor.validationErrors}
                            onDrop={handleDrop}
                            onRemoveTag={removeTag}
                            onRemoveColumn={removeColumn}
                            onRemoveRow={removeRow}
                            onCellEdit={(row, col, value) => {
                                setAssignor(prev => ({
                                    ...prev,
                                    data: prev.data.map((r, i) =>
                                        i === row ? r.map((cell, j) => j === col ? value : cell) : r
                                    ),
                                }));
                            }}
                        />
                    )}
                </div>

                {/* Dialog de salvar template */}
                {showSaveDialog && assignor.data.length > 0 && (
                    <SaveTemplateDialog
                        onSave={saveTemplate}
                        onClose={() => setShowSaveDialog(false)}
                    />
                )}
            </div>
        </div>
    );
}
